import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';
import { useBrandName } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';

const FLAG_ACRONYMS = { ai: 'AI', mfa: 'MFA', pdf: 'PDF', csv: 'CSV', sso: 'SSO', ui: 'UI', api: 'API', llm: 'LLM', sql: 'SQL', byo: 'BYO', vcs: 'VCS', sdk: 'SDK' };
const formatFlagName = (name) =>
  (name || '').replace(/_/g, ' ').split(' ')
    .map(w => FLAG_ACRONYMS[w.toLowerCase()] || (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import {
  User, Building2, Shield, Moon, Sun, Key, Flag,
  Smartphone, CheckCircle2, Loader2, Activity, HelpCircle, Pencil, Trash2, MessageSquare, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import ThemeManager from '@/components/ThemeManager';
import TenantDataExport from '@/components/TenantDataExport';
import JiraSettingsCard from '@/components/JiraSettingsCard';

const SLACK_APP_MANIFEST = `display_information:
  name: INZARA
  description: BI alerts and report links from INZARA
  background_color: "#0a0a0b"
features:
  bot_user:
    display_name: INZARA
    always_online: true
oauth_config:
  scopes:
    bot:
      - chat:write
      - chat:write.public
      - channels:read
      - groups:read
      - links:write
      - files:write
settings:
  org_deploy_enabled: false
  socket_mode_enabled: false
  token_rotation_enabled: false`;

const SETUP_INSTRUCTIONS = {
  slack_webhook: {
    title: 'Slack Incoming Webhook Setup (recommended)',
    steps: [
      'Go to https://api.slack.com/apps and click "Create New App"',
      'Choose "From an app manifest", select your workspace, and click Next',
      'In the manifest editor, paste the manifest below (or use "From scratch" → Incoming Webhooks)',
      'Click Next → Create',
      'In the left sidebar, click "Incoming Webhooks" and toggle Activate to ON',
      'Click "Add New Webhook to Workspace", choose a channel, then Allow',
      'Copy the Webhook URL (https://hooks.slack.com/services/...) and paste it above',
    ],
    manifest: SLACK_APP_MANIFEST,
    note: 'This is the simplest path — fully supported, no token rotation, no per-channel /invite needed. Recommended for most teams.',
  },
  slack_api: {
    title: 'Slack OAuth App Setup (Bot Token)',
    steps: [
      'Go to https://api.slack.com/apps and click "Create New App"',
      'Choose "From an app manifest", select your workspace',
      'Paste the manifest below — it pre-configures all required Bot Token Scopes (chat:write, chat:write.public, channels:read, groups:read, links:write, files:write)',
      'Click Next → Create',
      'Click "Install to Workspace" → "Allow"',
      'Open "OAuth & Permissions" and copy the "Bot User OAuth Token" (xoxb-...)',
      'Paste it in the Bot Token field above and click Save',
      'For private channels, invite the bot once with /invite @INZARA — chat:write.public lets you skip /invite for public channels',
      'Now everywhere you deliver notifications (Executive Summaries, Morning Brief, Alerts) you can pick channels from a dropdown instead of pasting webhook URLs',
    ],
    manifest: SLACK_APP_MANIFEST,
    note: 'The channels:read and groups:read scopes let INZARA list your Slack channels so you choose them from a dropdown (no webhook URLs). chat:write posts the message; chat:write.public avoids /invite for public channels. The legacy "Bots" custom integration was retired by Slack in 2018 — this OAuth-manifest flow is the current supported replacement.',
  },
  teams_webhook: {
    title: 'Microsoft Teams Webhook Setup',
    steps: [
      'Open Microsoft Teams and go to the channel where you want INZARA notifications to land',
      'Click the "•••" (More options) icon next to the channel name',
      'Choose "Workflows" (newer Teams) or "Connectors" (legacy Teams)',
      'In the workflow gallery, search for "Post to a channel when a webhook request is received" and click it',
      'Name the workflow (e.g. "INZARA Alerts"), confirm the team and channel, then click "Add workflow"',
      'Microsoft generates a unique POST URL — copy it',
      'Paste the URL into the Webhook URL field above and click Save',
      'Click "Send Test Teams Message" to verify — a card should appear in the channel within a few seconds',
    ],
    note: 'The legacy "Office 365 Connectors" path is being retired by Microsoft in 2025. The Workflows-based incoming webhook is the supported replacement and works the same way for our purposes — paste the generated URL and save.',
  },
  teams_api: {
    title: 'Microsoft Teams Graph API Setup',
    steps: [
      'Go to https://portal.azure.com → Azure Active Directory → App registrations → "New registration"',
      'Name it "INZARA", choose "Accounts in this organizational directory only", click Register',
      'On the app overview page, copy the Application (client) ID and Directory (tenant) ID',
      'Open "Certificates & secrets" → "Client secrets" → "New client secret" → choose an expiry → copy the secret VALUE (you only see it once)',
      'Open "API permissions" → "Add a permission" → "Microsoft Graph" → "Application permissions"',
      'Add ChannelMessage.Send and Group.ReadWrite.All, then click "Grant admin consent for <tenant>"',
      'Open Microsoft Teams → right-click the target channel → "Get link to channel" — extract the Team ID (groupId) and Channel ID (channelId) from the URL',
      'Enter Client ID, Client Secret, Tenant ID, Team ID, and Channel ID in the fields above and click Save',
      'Click "Send Test Teams Message" to verify',
    ],
    note: 'To pick Teams channels from a dropdown (instead of pasting a webhook), connect the INZARA Teams bot in the "AI in Microsoft Teams" card below and @mention it once in each channel you want to post to — Microsoft only permits posting to a channel through an installed bot, not app-only Graph credentials. This Graph API mode still works for a single fixed Team ID + Channel ID if you prefer.',
  },
  twilio: {
    title: 'Twilio SMS Setup',
    steps: [
      'Sign up at https://www.twilio.com and verify your account',
      'From the Twilio Console dashboard, copy your Account SID and Auth Token',
      'Go to "Phone Numbers" → "Manage" → "Buy a number" (or use your existing number)',
      'Copy the Twilio phone number (in +1XXXXXXXXXX format)',
      'Enter Account SID, Auth Token, and From Number in the fields above',
      'Click Save, then Test (you\'ll need to enter a recipient phone number)',
      'Note: Trial accounts can only send to verified phone numbers',
    ],
  },
};

export default function SettingsPage() {
  const APP = useBrandName();
  const { user, setUserData } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [mfaStatus, setMfaStatus] = useState({ mfa_enabled: false, mfa_pending: false });
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [flags, setFlags] = useState({});
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifConfig, setNotifConfig] = useState({});
  const [hideNonprod, setHideNonprod] = useState(!!user?.preferences?.hide_nonprod_reports);
  const toggleHideNonprod = async (val) => {
    setHideNonprod(val);
    try {
      const updated = await api.updateProfile({ hide_nonprod_reports: val });
      if (setUserData) setUserData(updated);
      localStorage.setItem('insightsStageFilter', val ? 'production' : 'all');
      toast.success(val ? 'Dev/test reports will be hidden' : 'Showing all report stages');
    } catch (err) { setHideNonprod(!val); toast.error(err.message || 'Could not update preference'); }
  };
  const [emailSetupOpen, setEmailSetupOpen] = useState(false);
  const [notifChannels, setNotifChannels] = useState({});
  const [notifSaving, setNotifSaving] = useState(false);
  const [setupGuide, setSetupGuide] = useState(null);
  // Distribution Lists
  const [distLists, setDistLists] = useState([]);
  const [editDL, setEditDL] = useState(null);
  const [dlName, setDlName] = useState('');
  const [dlEmails, setDlEmails] = useState('');
  const [dlDesc, setDlDesc] = useState('');
  const [dlDialogOpen, setDlDialogOpen] = useState(false);
  // Organization editing
  const [editingOrg, setEditingOrg] = useState(false);
  const [orgForm, setOrgForm] = useState({ company: '', industry: '', description: '' });
  const [orgSaving, setOrgSaving] = useState(false);
  // AI in Slack (/cloverleaf bot)
  const [slackAi, setSlackAi] = useState(null);
  const [slackAiBusy, setSlackAiBusy] = useState(false);
  const [slackGuideOpen, setSlackGuideOpen] = useState(false);
  const [slackTestChannel, setSlackTestChannel] = useState('');
  const [slackTestBusy, setSlackTestBusy] = useState(false);
  // AI in Microsoft Teams (Bot Framework)
  const [teamsAi, setTeamsAi] = useState(null);
  const [teamsAiBusy, setTeamsAiBusy] = useState(false);
  const [teamsTestBusy, setTeamsTestBusy] = useState(false);
  const [teamsDiag, setTeamsDiag] = useState(null);
  const [teamsGuideOpen, setTeamsGuideOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [status, ff] = await Promise.all([
          api.getMfaStatus().catch(() => ({ mfa_enabled: false })),
          api.getFeatureFlags().catch(() => ({})),
        ]);
        setMfaStatus(status);
        setFlags(ff);
        try { setMetricsData(await api.getMetrics()); } catch (e) { console.warn('Failed to load metrics:', e.message); }
        try { setNotifConfig(await api.getNotificationConfig()); } catch (e) { console.warn('Failed to load notification config:', e.message); }
        try { setNotifChannels(await api.getNotificationChannels()); } catch (e) { console.warn('Failed to load notification channels:', e.message); }
        try {
          const org = await api.getOrganization();
          if (org) setOrgForm({ company: org.company || '', industry: org.industry || '', description: org.description || '' });
        } catch (e) { console.warn('Failed to load org:', e.message); }
        try { setDistLists(await api.listDistributionLists()); } catch (e) { console.warn('Failed to load distribution lists:', e.message); }
        try { setSlackAi(await api.getSlackAiConfig()); } catch (e) { console.warn('Failed to load Slack AI config:', e.message); }
        try { setTeamsAi(await api.getTeamsAiConfig()); } catch (e) { console.warn('Failed to load Teams AI config:', e.message); }
      } catch (e) { console.error('Settings load failed:', e.message); } finally { setLoading(false); }
    };
    load();
  }, [user]);

  const saveNotifConfig = async () => {
    setNotifSaving(true);
    try {
      await api.updateNotificationConfig(notifConfig);
      setNotifChannels(await api.getNotificationChannels());
      toast.success('Notification settings saved');
    } catch (err) { toast.error(err.message); }
    finally { setNotifSaving(false); }
  };

  const connectSlackAi = async () => {
    setSlackAiBusy(true);
    try {
      // Persist the latest bot token + signing secret first so the backend can verify.
      await api.updateNotificationConfig(notifConfig);
      const res = await api.connectSlackAi();
      setSlackAi(await api.getSlackAiConfig());
      toast.success(`Connected to Slack workspace "${res.team_name || res.team_id}"`);
    } catch (err) { toast.error(err.message || 'Slack connect failed'); }
    finally { setSlackAiBusy(false); }
  };

  const disconnectSlackAi = async () => {
    setSlackAiBusy(true);
    try {
      await api.disconnectSlackAi();
      setSlackAi(await api.getSlackAiConfig());
      toast.success('Slack workspace disconnected');
    } catch (err) { toast.error(err.message); }
    finally { setSlackAiBusy(false); }
  };

  const sendSlackTest = async () => {
    setSlackTestBusy(true);
    try {
      await api.updateNotificationConfig(notifConfig);
      const res = await api.slackTestMessage(slackTestChannel);
      if (res.ok) toast.success(res.message);
      else toast.error(res.message);
    } catch (err) { toast.error(err.message || 'Slack test failed'); }
    finally { setSlackTestBusy(false); }
  };

  const copyText = (t) => {
    navigator.clipboard?.writeText(t);
    toast.success('Copied');
  };

  const connectTeamsAi = async () => {
    setTeamsAiBusy(true);
    try {
      await api.updateNotificationConfig(notifConfig);
      const res = await api.connectTeamsAi();
      setTeamsAi(await api.getTeamsAiConfig());
      toast.success(`Connected Teams bot (App ${res.app_id?.slice(0, 8)}…)`);
    } catch (err) { toast.error(err.message || 'Teams connect failed'); }
    finally { setTeamsAiBusy(false); }
  };

  const disconnectTeamsAi = async () => {
    setTeamsAiBusy(true);
    try {
      await api.disconnectTeamsAi();
      setTeamsAi(await api.getTeamsAiConfig());
      toast.success('Teams bot disconnected');
    } catch (err) { toast.error(err.message); }
    finally { setTeamsAiBusy(false); }
  };

  const sendTeamsTest = async () => {
    setTeamsTestBusy(true);
    try {
      await api.updateNotificationConfig(notifConfig);
      const res = await api.teamsTestMessage();
      if (res.ok) { toast.success(res.message); setTeamsDiag(null); }
      else { toast.error(res.message); setTeamsDiag(res.diagnosis || null); }
    } catch (err) { toast.error(err.message || 'Teams test failed'); }
    finally { setTeamsTestBusy(false); }
  };

  const runTeamsDiag = async () => {
    setTeamsTestBusy(true);
    try {
      await api.updateNotificationConfig(notifConfig);
      const res = await api.diagnoseTeamsAi();
      setTeamsDiag(res);
      if (res.ok) toast.success(res.recommendation || 'Credentials look valid');
      else toast.error('Found a configuration issue — see the details below');
    } catch (err) { toast.error(err.message || 'Teams diagnosis failed'); }
    finally { setTeamsTestBusy(false); }
  };

  const downloadTeamsManifest = async () => {
    try {
      const m = await api.getTeamsManifest();
      const blob = new Blob([JSON.stringify(m, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'manifest.json'; a.click();
      URL.revokeObjectURL(url);
      toast.success('manifest.json downloaded — add color.png + outline.png and zip');
    } catch (err) { toast.error(err.message); }
  };

  const downloadTeamsAppPackage = async () => {
    try {
      const blob = await api.downloadTeamsAppPackage();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'cloverleaf-teams-app.zip'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Teams app package downloaded — upload this .zip directly to Teams');
    } catch (err) { toast.error(err.message); }
  };

  const testChannel = async (channel) => {    try {
      const res = await api.testNotification(channel, { email: user?.email });
      if (res.success) {
        // Email-specific success toast surfaces the provider Message ID + spam-check tip
        if (channel === 'email' && res.message_id) {
          toast.success(`Test email sent to ${res.to}`, {
            description: `Message ID: ${res.message_id} · From: ${res.from} · If you don't see it in 1-2 min, check spam, then verify in your ${res.service === 'sendgrid' ? 'SendGrid' : 'Resend'} dashboard.`,
            duration: 12000,
          });
        } else if (channel === 'email') {
          toast.success(`Test email accepted for ${res.to || user?.email}`, {
            description: 'If you do not receive it within a couple of minutes, check spam and your email provider dashboard.',
            duration: 10000,
          });
        } else {
          toast.success(`${channel} test sent`);
        }
      } else if (res.fix_url) {
        // Sender verification problem — actionable error with a link
        toast.error(`${channel} test failed: ${res.error}`, {
          action: { label: 'Fix verification', onClick: () => window.open(res.fix_url, '_blank', 'noopener') },
          duration: 20000,
        });
      } else if (channel === 'slack') {
        // Slack diagnostic — surface the actionable hint message + raw error code
        toast.error(`Slack test failed: ${res.message || res.error || 'Unknown error'}`, {
          description: res.error && res.error !== res.message ? `Slack returned: ${res.error}` : undefined,
          duration: 20000,
        });
      } else {
        const detail = res.body || res.message || res.error || 'Unknown error';
        toast.error(`${channel} test failed: ${detail}`, { duration: 12000 });
      }
    } catch (err) { toast.error(err.message); }
  };

  const startMfaSetup = async () => {
    setMfaLoading(true);
    try {
      const result = await api.enableMfa();
      setMfaSetup(result);
      setMfaDialogOpen(true);
    } catch (err) { toast.error(err.message); }
    finally { setMfaLoading(false); }
  };

  const confirmMfa = async () => {
    setMfaLoading(true);
    try {
      await api.confirmMfa(mfaCode);
      setMfaStatus({ mfa_enabled: true, mfa_pending: false });
      setMfaDialogOpen(false);
      setMfaCode('');
      toast.success('MFA enabled successfully');
    } catch (err) { toast.error(err.message); }
    finally { setMfaLoading(false); }
  };

  const handleDisableMfa = async () => {
    try {
      await api.disableMfa();
      setMfaStatus({ mfa_enabled: false, mfa_pending: false });
      toast.success('MFA disabled');
    } catch (err) { toast.error(err.message); }
  };

  const toggleFlag = async (flagName, currentEnabled) => {
    try {
      await api.updateFeatureFlag(flagName, !currentEnabled);
      setFlags(prev => ({
        ...prev,
        [flagName]: { ...prev[flagName], enabled: !currentEnabled }
      }));
      toast.success(`Flag ${flagName} ${!currentEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error(err.message); }
  };

  const saveOrg = async () => {
    setOrgSaving(true);
    try {
      await api.updateOrganization(orgForm);
      setEditingOrg(false);
      toast.success('Organization updated');
    } catch (err) { toast.error(err.message); }
    finally { setOrgSaving(false); }
  };

  const openCreateDL = () => { setEditDL(null); setDlName(''); setDlEmails(''); setDlDesc(''); setDlDialogOpen(true); };
  const openEditDL = (dl) => { setEditDL(dl); setDlName(dl.name); setDlEmails((dl.emails || []).join(', ')); setDlDesc(dl.description || ''); setDlDialogOpen(true); };
  const saveDL = async () => {
    try {
      const emails = dlEmails.split(',').map(e => e.trim()).filter(Boolean);
      if (editDL) {
        const updated = await api.updateDistributionList(editDL.list_id, { name: dlName, emails, description: dlDesc });
        setDistLists(prev => prev.map(d => d.list_id === editDL.list_id ? updated : d));
      } else {
        const created = await api.createDistributionList({ name: dlName, emails, description: dlDesc });
        setDistLists(prev => [...prev, created]);
      }
      setDlDialogOpen(false);
      toast.success(editDL ? 'List updated' : 'List created');
    } catch (err) { toast.error(err.message); }
  };
  const deleteDL = async (id) => {
    try { await api.deleteDistributionList(id); setDistLists(prev => prev.filter(d => d.list_id !== id)); toast.success('List deleted'); }
    catch (err) { toast.error(err.message); }
  };

  const flagsByCategory = {};
  Object.entries(flags).forEach(([name, flag]) => {
    const cat = flag.category || 'other';
    if (!flagsByCategory[cat]) flagsByCategory[cat] = [];
    flagsByCategory[cat].push({ name, ...flag });
  });

  return (
    <div className="p-6 max-w-3xl space-y-8" data-testid="settings-page">
      <div className="animate-slide-up">
        <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account and platform preferences</p>
      </div>

      {/* Organization */}
      <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '100ms' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <h2 className="text-base font-heading font-semibold text-foreground">Organization</h2>
          </div>
          {user?.role === 'admin' || user?.role === 'super_admin' ? (
            !editingOrg ? (
              <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={() => setEditingOrg(true)} data-testid="edit-org-btn">Edit</Button>
            ) : (
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={() => setEditingOrg(false)}>Cancel</Button>
                <Button size="sm" className="rounded-lg text-xs h-7" onClick={saveOrg} disabled={orgSaving} data-testid="save-org-btn">
                  {orgSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save
                </Button>
              </div>
            )
          ) : null}
        </div>
        <Separator className="bg-border/50" />
        {editingOrg ? (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-muted-foreground text-xs mb-1">Company Name</Label>
              <Input value={orgForm.company} onChange={e => setOrgForm(p => ({ ...p, company: e.target.value }))} className="h-8 text-sm" data-testid="org-company-input" />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs mb-1">Industry</Label>
              <Input value={orgForm.industry} onChange={e => setOrgForm(p => ({ ...p, industry: e.target.value }))} className="h-8 text-sm" data-testid="org-industry-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-muted-foreground text-xs mb-1">Description</Label>
              <Input value={orgForm.description} onChange={e => setOrgForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of your organization" className="h-8 text-sm" data-testid="org-description-input" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-muted-foreground text-xs mb-1">Company</p><p className="text-foreground font-medium" data-testid="org-company">{orgForm.company || 'Not set'}</p></div>
            <div><p className="text-muted-foreground text-xs mb-1">Industry</p><p className="text-foreground font-medium" data-testid="org-industry">{orgForm.industry || 'Not set'}</p></div>
            {orgForm.description && <div className="col-span-2"><p className="text-muted-foreground text-xs mb-1">Description</p><p className="text-foreground font-medium">{orgForm.description}</p></div>}
          </div>
        )}
      </div>

      {/* Jira Integration (server-wide, super admin only) */}
      {user?.role === 'super_admin' && <JiraSettingsCard />}

      {/* Distribution Lists */}
      <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '125ms' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <h2 className="text-base font-heading font-semibold text-foreground">Distribution Lists</h2>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={openCreateDL} data-testid="create-dl-btn">New List</Button>
        </div>
        <Separator className="bg-border/50" />
        {distLists.length === 0 ? (
          <p className="text-xs text-muted-foreground/70 italic">No distribution lists. Create one to use in scheduled reports.</p>
        ) : (
          <div className="space-y-2">
            {distLists.map(dl => (
              <div key={dl.list_id} className="flex items-center justify-between p-3 rounded-lg border border-border/30 group" data-testid={`dl-${dl.list_id}`}>
                <div>
                  <p className="text-sm font-medium text-foreground">{dl.name}</p>
                  <p className="text-[10px] text-muted-foreground">{(dl.emails || []).length} recipients{dl.description ? ` — ${dl.description}` : ''}</p>
                  <p className="text-[9px] text-muted-foreground/70 mt-0.5 truncate max-w-sm">{(dl.emails || []).join(', ')}</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEditDL(dl)} className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => deleteDL(dl.list_id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appearance */}
      <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '150ms' }}>
        <div className="flex items-center gap-3">
          {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" strokeWidth={1.5} /> : <Sun className="w-5 h-5 text-primary" strokeWidth={1.5} />}
          <h2 className="text-base font-heading font-semibold text-foreground">Appearance</h2>
        </div>
        <Separator className="bg-border/50" />
        <div className="flex items-center justify-between">
          <div><Label className="text-sm text-foreground">Dark Mode</Label><p className="text-xs text-muted-foreground mt-0.5">Toggle between light and dark themes</p></div>
          <Switch data-testid="dark-mode-toggle" checked={theme === 'dark'} onCheckedChange={toggleTheme} />
        </div>
        <Separator className="bg-border/50" />
        <div className="flex items-center justify-between">
          <div><Label className="text-sm text-foreground">Hide dev/test reports</Label><p className="text-xs text-muted-foreground mt-0.5">Keep the Insights list to production reports by default</p></div>
          <Switch data-testid="hide-nonprod-toggle" checked={hideNonprod} onCheckedChange={toggleHideNonprod} />
        </div>
      </div>

      {/* MFA (E2-S3) */}
      <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '200ms' }}>
        <div className="flex items-center gap-3">
          <Smartphone className="w-5 h-5 text-primary" strokeWidth={1.5} />
          <h2 className="text-base font-heading font-semibold text-foreground">Multi-Factor Authentication</h2>
        </div>
        <Separator className="bg-border/50" />
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-foreground">TOTP Authenticator</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {mfaStatus.mfa_enabled ? 'MFA is active. Your account is protected.' : 'Add an extra layer of security with a TOTP authenticator app.'}
            </p>
          </div>
          {mfaStatus.mfa_enabled ? (
            <div className="flex items-center gap-2">
              <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Active
              </Badge>
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleDisableMfa} data-testid="disable-mfa-btn">
                Disable
              </Button>
            </div>
          ) : (
            <Button size="sm" className="rounded-lg text-xs bg-primary text-primary-foreground active:scale-95" onClick={startMfaSetup} disabled={mfaLoading} data-testid="enable-mfa-btn">
              {mfaLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Key className="w-3 h-3 mr-1" />}
              Enable MFA
            </Button>
          )}
        </div>
      </div>

      {/* Color Themes */}
      <div className="glass-card rounded-2xl p-6 animate-slide-up" style={{ animationDelay: '215ms' }}>
        <ThemeManager />
      </div>

      {/* Notification Integrations */}
      {(user?.role === 'admin' || user?.role === 'tenant_admin' || user?.role === 'super_admin') && (
        <div id="notifications" className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up scroll-mt-6" style={{ animationDelay: '225ms' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="text-base font-heading font-semibold text-foreground">Notification Integrations</h2>
            </div>
            <div className="flex gap-1">
              {notifChannels.email && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Email</Badge>}
              {notifChannels.slack && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Slack</Badge>}
              {notifChannels.teams && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Teams</Badge>}
              {notifChannels.twilio && <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Twilio</Badge>}
            </div>
          </div>
          {/* Tenant-scope hint — every tenant configures their own credentials. */}
          <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 flex items-start gap-2" data-testid="notif-tenant-scope-hint">
            <Shield className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-foreground/75 leading-relaxed">
              These credentials are scoped to <span className="font-semibold text-foreground">your tenant</span> only. Other tenants — including the platform Super Admin — set up their own Slack and Teams connections independently.
            </p>
          </div>
          <Separator className="bg-border/50" />

          {/* SendGrid Email */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-heading font-semibold text-foreground">Email</p>
              <div className="flex items-center gap-1.5">
                {user?.role !== 'super_admin' && (
                  <>
                    <button
                      onClick={() => setNotifConfig(c => ({ ...c, email_provider: 'platform' }))}
                      className={`px-2 py-0.5 rounded text-[9px] transition-colors ${(notifConfig.email_provider || 'platform') === 'platform' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid="email-provider-platform-btn"
                      title={`Use ${APP}'s email service — no setup required`}
                    >Use {APP}</button>
                    <button
                      onClick={() => setNotifConfig(c => ({ ...c, email_provider: 'custom' }))}
                      className={`px-2 py-0.5 rounded text-[9px] transition-colors ${notifConfig.email_provider === 'custom' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid="email-provider-custom-btn"
                      title="Use your own Resend or SendGrid account"
                    >Own account</button>
                  </>
                )}
                {notifChannels.email ? <Badge className="text-[7px] bg-emerald-600 text-white">Connected</Badge> : <Badge variant="outline" className="text-[7px] text-muted-foreground">Not configured</Badge>}
              </div>
            </div>
            {/* Super admin configures the platform-level email account on the
                system tenant. Regular tenants choose between using that
                (default) or their own Resend/SendGrid account. */}
            {user?.role !== 'super_admin' && (notifConfig.email_provider || 'platform') === 'platform' ? (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-[11px] text-foreground/80 leading-relaxed" data-testid="email-platform-info">
                Emails will be sent via {APP}'s managed email service — no API key needed. If your platform admin has not configured the service yet, email delivery will be disabled. Switch to <span className="font-semibold">Own account</span> to connect your own Resend or SendGrid account.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <Label className="text-[10px] text-muted-foreground">Service</Label>
                  {['resend', 'sendgrid'].map(svc => (
                    <button
                      key={svc}
                      onClick={() => setNotifConfig(c => ({ ...c, email_service: svc }))}
                      className={`px-2 py-0.5 rounded text-[9px] capitalize transition-colors ${((notifConfig.email_service || (notifConfig.sendgrid_api_key && !notifConfig.resend_api_key ? 'sendgrid' : 'resend'))) === svc ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground border border-border/50'}`}
                      data-testid={`email-service-${svc}-btn`}
                    >{svc === 'resend' ? 'Resend' : 'SendGrid'}</button>
                  ))}
                  <button
                    onClick={() => setEmailSetupOpen(true)}
                    className="ml-auto px-2 py-0.5 rounded text-[9px] text-primary hover:bg-primary/10 border border-primary/30 transition-colors"
                    data-testid="email-setup-instructions-btn"
                  >Setup instructions</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {((notifConfig.email_service || (notifConfig.sendgrid_api_key && !notifConfig.resend_api_key ? 'sendgrid' : 'resend'))) === 'sendgrid' ? (
                    <div><Label className="text-[10px] text-muted-foreground">SendGrid API Key</Label><Input value={notifConfig.sendgrid_api_key || ''} onChange={e => setNotifConfig(c => ({ ...c, sendgrid_api_key: e.target.value }))} placeholder="SG.xxxx..." className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" type="password" data-testid="sendgrid-api-key-input" /></div>
                  ) : (
                    <div><Label className="text-[10px] text-muted-foreground">Resend API Key</Label><Input value={notifConfig.resend_api_key || ''} onChange={e => setNotifConfig(c => ({ ...c, resend_api_key: e.target.value }))} placeholder="re_xxxx..." className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" type="password" data-testid="resend-api-key-input" /></div>
                  )}
                  <div><Label className="text-[10px] text-muted-foreground">Sender Email</Label><Input value={notifConfig.sender_email || ''} onChange={e => setNotifConfig(c => ({ ...c, sender_email: e.target.value }))} placeholder="noreply@yourcompany.com" className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5" data-testid="sender-email-input" /></div>
                </div>
                <div><Label className="text-[10px] text-muted-foreground">Sender Name <span className="text-muted-foreground/60">(display name)</span></Label><Input value={notifConfig.sender_name || ''} onChange={e => setNotifConfig(c => ({ ...c, sender_name: e.target.value }))} placeholder={`${APP} Notifications`} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5" data-testid="sender-name-input" /><p className="text-[9px] text-muted-foreground mt-0.5">Shown as the "From" name so recipients know who the email is from — e.g. "{notifConfig.sender_name || `${APP} Notifications`}" &lt;{notifConfig.sender_email || 'noreply@yourcompany.com'}&gt;</p></div>
                <p className="text-[10px] text-muted-foreground">
                  {((notifConfig.email_service || (notifConfig.sendgrid_api_key && !notifConfig.resend_api_key ? 'sendgrid' : 'resend'))) === 'sendgrid'
                    ? 'Get a key at sendgrid.com → API Keys. Verify your sender at Settings → Sender Authentication.'
                    : 'Get a key at resend.com → API Keys. Verify your domain at resend.com/domains (or use onboarding@resend.dev to test).'}
                </p>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => testChannel('email')} className="rounded-lg text-[10px] h-7" data-testid="test-email-btn">Send Test Email</Button>
            <div className="flex items-center justify-between rounded-lg border border-border/40 px-3 py-2">
              <div>
                <p className="text-[11px] font-medium text-foreground">Onboarding & tips emails</p>
                <p className="text-[9px] text-muted-foreground">Short getting-started series sent to workspace admins during the first week.</p>
              </div>
              <Switch
                checked={notifConfig.onboarding_emails_enabled !== false}
                onCheckedChange={(v) => setNotifConfig(c => ({ ...c, onboarding_emails_enabled: v }))}
                data-testid="onboarding-emails-toggle"
              />
            </div>
          </div>

          {emailSetupOpen && (
            <Dialog open onOpenChange={(o) => !o && setEmailSetupOpen(false)}>
              <DialogContent className="max-w-lg" data-testid="email-setup-dialog">
                <DialogHeader>
                  <DialogTitle>
                    {((notifConfig.email_service || (notifConfig.sendgrid_api_key && !notifConfig.resend_api_key ? 'sendgrid' : 'resend'))) === 'sendgrid'
                      ? 'Set up your own SendGrid account' : 'Set up your own Resend account'}
                  </DialogTitle>
                </DialogHeader>
                {((notifConfig.email_service || (notifConfig.sendgrid_api_key && !notifConfig.resend_api_key ? 'sendgrid' : 'resend'))) === 'sendgrid' ? (
                  <ol className="text-xs space-y-2.5 list-decimal pl-4 text-foreground/90 leading-relaxed" data-testid="sendgrid-setup-steps">
                    <li>Create a free account at <a href="https://signup.sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">signup.sendgrid.com</a>.</li>
                    <li>Go to <span className="font-semibold">Settings → API Keys → Create API Key</span>. Choose <span className="font-semibold">Restricted Access</span> and enable only <span className="font-mono">Mail Send</span>. Copy the key (starts with <span className="font-mono">SG.</span>).</li>
                    <li>Verify your sender: <span className="font-semibold">Settings → Sender Authentication</span>. Either verify a <span className="font-semibold">Single Sender</span> (fastest — just confirm an email) or authenticate your whole domain via DNS records (best deliverability).</li>
                    <li>Paste the API key and your verified sender address into the fields here, then <span className="font-semibold">Save</span>.</li>
                    <li>Click <span className="font-semibold">Send Test Email</span> — INZARA also pre-checks sender verification and will warn you if SendGrid would silently drop your mail.</li>
                  </ol>
                ) : (
                  <ol className="text-xs space-y-2.5 list-decimal pl-4 text-foreground/90 leading-relaxed" data-testid="resend-setup-steps">
                    <li>Create a free account at <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com</a>.</li>
                    <li>Go to <span className="font-semibold">API Keys → Create API Key</span> ("Sending access" is enough). Copy the key (starts with <span className="font-mono">re_</span>).</li>
                    <li>Verify your domain: <span className="font-semibold">Domains → Add Domain</span>, then add the DNS records Resend shows you (SPF + DKIM). Until it's verified you can test with the shared sender <span className="font-mono">onboarding@resend.dev</span>, which can only email your own address.</li>
                    <li>Paste the API key and your sender address (e.g. <span className="font-mono">noreply@yourcompany.com</span>) into the fields here, then <span className="font-semibold">Save</span>.</li>
                    <li>Click <span className="font-semibold">Send Test Email</span> to confirm delivery.</li>
                    <li className="text-muted-foreground">Optional — receive emails too (Email-to-Insight): enable <span className="font-semibold">Receiving</span> on your domain in Resend, then add a webhook for the <span className="font-mono">email.received</span> event pointing at <span className="font-mono">/api/inbound/resend</span> (full URL shown under Admin → Inbound Emails).</li>
                  </ol>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Your API key is stored encrypted per-tenant and only used to send email on your behalf. Switch back to <span className="font-semibold">Use INZARA</span> anytime.
                </p>
              </DialogContent>
            </Dialog>
          )}
          <Separator className="bg-border/30" />

          {/* Slack */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-heading font-semibold text-foreground">Slack</p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setSetupGuide((notifConfig.slack_mode || 'webhook') === 'api' ? 'slack_api' : 'slack_webhook')}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-medium transition-colors"
                  title="View step-by-step Slack setup guide"
                  data-testid="slack-setup-help-btn"
                >
                  <HelpCircle className="w-3 h-3" /> Setup guide
                </button>
                <button onClick={() => setNotifConfig(c => ({ ...c, slack_mode: 'webhook' }))} className={`px-2 py-0.5 rounded text-[9px] ${(notifConfig.slack_mode || 'webhook') === 'webhook' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}>Webhook</button>
                <button onClick={() => setNotifConfig(c => ({ ...c, slack_mode: 'api' }))} className={`px-2 py-0.5 rounded text-[9px] ${notifConfig.slack_mode === 'api' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}>Bot API</button>
              </div>
            </div>
            {(notifConfig.slack_mode || 'webhook') === 'webhook' ? (
              <div><Label className="text-[10px] text-muted-foreground">Webhook URL</Label><Input value={notifConfig.slack_webhook_url || ''} onChange={e => setNotifConfig(c => ({ ...c, slack_webhook_url: e.target.value }))} placeholder="https://hooks.slack.com/services/..." className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
            ) : (
              <><div><Label className="text-[10px] text-muted-foreground">Bot Token</Label><Input value={notifConfig.slack_bot_token || ''} onChange={e => setNotifConfig(c => ({ ...c, slack_bot_token: e.target.value }))} placeholder="xoxb-..." className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" type="password" /></div>
              <div><Label className="text-[10px] text-muted-foreground">Default Channel</Label><Input value={notifConfig.slack_default_channel || ''} onChange={e => setNotifConfig(c => ({ ...c, slack_default_channel: e.target.value }))} placeholder="#general" className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5" /></div></>
            )}
            <Button variant="outline" size="sm" onClick={() => testChannel('slack')} className="rounded-lg text-[10px] h-7">Send Test Slack Message</Button>
            {/* Slack signing secret — required for interactive button clicks (Approve/Decline) on signup notifications. */}
            <div>
              <Label className="text-[10px] text-muted-foreground flex items-center gap-1">
                Signing Secret <span className="text-[8px] text-muted-foreground/80">(optional — enables interactive buttons)</span>
              </Label>
              <Input
                type="password"
                value={notifConfig.slack_signing_secret || ''}
                onChange={e => setNotifConfig(c => ({ ...c, slack_signing_secret: e.target.value }))}
                placeholder="Paste from Slack App → Basic Information → Signing Secret"
                className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono"
                data-testid="slack-signing-secret"
              />
              <p className="text-[9px] text-muted-foreground/70 mt-0.5">
                Set this if you want admins to approve/decline signup requests directly from Slack. Configure your Slack App&apos;s Interactivity Request URL to <span className="font-mono">/api/integrations/slack/interactivity</span>.
              </p>
            </div>
          </div>
          <Separator className="bg-border/30" />

          {/* AI in Slack — the /cloverleaf question-answering bot */}
          <div className="space-y-2" data-testid="ai-in-slack-card">
            <div className="flex items-center justify-between">
              <p className="text-xs font-heading font-semibold text-foreground flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-primary" /> AI in Slack
                <span className="text-[9px] font-normal text-muted-foreground/70">— ask questions with <span className="font-mono">/cloverleaf</span></span>
              </p>
              <div className="flex items-center gap-1.5">
                {slackAi?.connected ? (
                  <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20" data-testid="slack-ai-status">
                    Connected · {slackAi.team_name || slackAi.team_id}
                  </Badge>
                ) : (
                  <Badge className="text-[8px] bg-muted text-muted-foreground border-border/40" data-testid="slack-ai-status">Not connected</Badge>
                )}
                <button
                  onClick={() => setSlackGuideOpen(o => !o)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-medium transition-colors"
                  data-testid="slack-ai-setup-help-btn"
                >
                  <HelpCircle className="w-3 h-3" /> {slackGuideOpen ? 'Hide guide' : 'Setup guide'}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Let your team ask questions in Slack and get governed answers with charts. Uses the <span className="font-semibold">Bot Token</span> and <span className="font-semibold">Signing Secret</span> from the Slack section above — enter them, Save, then click <span className="font-semibold">Connect &amp; Verify</span>.
            </p>

            {/* Request URLs to paste into the Slack app */}
            {slackAi?.urls && (
              <div className="rounded-lg bg-muted/40 border border-border/40 p-2.5 space-y-1.5">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/80 font-semibold">Request URLs (paste into your Slack app)</p>
                {[
                  ['Slash command', slackAi.urls.commands],
                  ['Event Subscriptions', slackAi.urls.events],
                  ['Interactivity', slackAi.urls.interactivity],
                ].map(([label, url]) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="text-[9px] text-muted-foreground w-28 shrink-0">{label}</span>
                    <code className="text-[9px] font-mono text-foreground/80 truncate flex-1 bg-background/60 rounded px-1.5 py-0.5">{url}</code>
                    <button onClick={() => copyText(url)} className="text-muted-foreground hover:text-primary" data-testid={`copy-slack-url-${label.split(' ')[0].toLowerCase()}`}><Copy className="w-3 h-3" /></button>
                  </div>
                ))}
                <p className="text-[9px] text-muted-foreground/70 pt-0.5">
                  Slash command: <span className="font-mono">{slackAi.slash_command}</span> · Bot scopes: <span className="font-mono">{(slackAi.scopes || []).join(', ')}</span>
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={connectSlackAi}
                disabled={slackAiBusy}
                className="rounded-lg text-[10px] h-7"
                data-testid="slack-ai-connect-btn"
              >
                {slackAiBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {slackAi?.connected ? 'Re-verify connection' : 'Connect & Verify'}
              </Button>
              {slackAi?.connected && (
                <Button variant="outline" size="sm" onClick={disconnectSlackAi} disabled={slackAiBusy} className="rounded-lg text-[10px] h-7" data-testid="slack-ai-disconnect-btn">
                  Disconnect
                </Button>
              )}
            </div>

            {slackAi?.connected && (
              <div className="flex items-center gap-2" data-testid="slack-test-row">
                <Input
                  value={slackTestChannel}
                  onChange={e => setSlackTestChannel(e.target.value)}
                  placeholder="#channel or channel ID"
                  className="rounded-lg bg-muted/50 border-border/50 text-xs h-7 flex-1 max-w-[200px] font-mono"
                  data-testid="slack-test-channel"
                />
                <Button variant="outline" size="sm" onClick={sendSlackTest} disabled={slackTestBusy || !slackTestChannel.trim()} className="rounded-lg text-[10px] h-7" data-testid="slack-test-btn">
                  {slackTestBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Send test message
                </Button>
                <span className="text-[9px] text-muted-foreground/70">Invite the bot to the channel first.</span>
              </div>
            )}

            {slackGuideOpen && (
              <div className="rounded-lg bg-background/60 border border-border/40 p-3 text-[10px] text-muted-foreground leading-relaxed space-y-1.5" data-testid="slack-ai-guide">
                <p className="text-foreground font-semibold text-[11px]">Step-by-step setup</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Go to <span className="font-mono">api.slack.com/apps</span> → <span className="font-semibold">Create New App</span> → From scratch. Name it and pick your workspace.</li>
                  <li>Under <span className="font-semibold">OAuth &amp; Permissions</span> → Bot Token Scopes, add: <span className="font-mono">{(slackAi?.scopes || []).join(', ')}</span>.</li>
                  <li>Click <span className="font-semibold">Install to Workspace</span>, then copy the <span className="font-semibold">Bot User OAuth Token</span> (<span className="font-mono">xoxb-…</span>) into the Slack section above.</li>
                  <li>Under <span className="font-semibold">Basic Information</span> → App Credentials, copy the <span className="font-semibold">Signing Secret</span> into the field above. Click <span className="font-semibold">Save</span>.</li>
                  <li>Under <span className="font-semibold">Slash Commands</span> → Create New Command: command <span className="font-mono">{slackAi?.slash_command}</span>, Request URL = the <span className="font-semibold">Slash command</span> URL above.</li>
                  <li>Under <span className="font-semibold">Event Subscriptions</span>: turn on, set Request URL = the <span className="font-semibold">Event Subscriptions</span> URL above, then subscribe to bot events <span className="font-mono">app_mention</span> and <span className="font-mono">message.im</span>.</li>
                  <li>Under <span className="font-semibold">Interactivity &amp; Shortcuts</span>: turn on and set Request URL = the <span className="font-semibold">Interactivity</span> URL above (required for chart-type, Drill by…, Investigate and Preview buttons).</li>
                  <li>Reinstall the app if Slack prompts, then come back here and click <span className="font-semibold">Connect &amp; Verify</span>.</li>
                  <li>In Slack, each user runs <span className="font-mono">{slackAi?.slash_command} link</span> once to connect their account and pick a model — answers then respect their permissions and PII governance.</li>
                </ol>
              </div>
            )}
          </div>
          <Separator className="bg-border/30" />

          {/* AI in Microsoft Teams — the conversational bot */}
          <div className="space-y-2" data-testid="ai-in-teams-card">
            <div className="flex items-center justify-between">
              <p className="text-xs font-heading font-semibold text-foreground flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-primary" /> AI in Microsoft Teams
                <span className="text-[9px] font-normal text-muted-foreground/70">— @mention or DM the bot</span>
              </p>
              <div className="flex items-center gap-1.5">
                {teamsAi?.connected ? (
                  <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20" data-testid="teams-ai-status">Connected</Badge>
                ) : (
                  <Badge className="text-[8px] bg-muted text-muted-foreground border-border/40" data-testid="teams-ai-status">Not connected</Badge>
                )}
                <button
                  onClick={() => setTeamsGuideOpen(o => !o)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-medium transition-colors"
                  data-testid="teams-ai-setup-help-btn"
                >
                  <HelpCircle className="w-3 h-3" /> {teamsGuideOpen ? 'Hide guide' : 'Setup guide'}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Ask questions in Teams and get governed answers with charts. Requires an <span className="font-semibold">Azure Bot registration</span> — enter its credentials below, Save, then <span className="font-semibold">Connect &amp; Verify</span>.
            </p>

            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px] text-muted-foreground">Microsoft App ID</Label><Input value={notifConfig.teams_bot_app_id || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_bot_app_id: e.target.value }))} placeholder="00000000-0000-0000-0000-000000000000" className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" data-testid="teams-bot-app-id" /></div>
              <div><Label className="text-[10px] text-muted-foreground">Client Secret</Label><Input type="password" value={notifConfig.teams_bot_app_password || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_bot_app_password: e.target.value }))} placeholder="client secret value" className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" data-testid="teams-bot-secret" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <div>
                <Label className="text-[10px] text-muted-foreground">App Type</Label>
                <div className="flex items-center gap-1 mt-0.5">
                  <button onClick={() => setNotifConfig(c => ({ ...c, teams_bot_app_type: 'SingleTenant' }))} className={`px-2 py-1 rounded text-[9px] flex-1 ${(notifConfig.teams_bot_app_type || 'SingleTenant') === 'SingleTenant' ? 'bg-primary/20 text-primary' : 'bg-muted/50 text-muted-foreground'}`} data-testid="teams-apptype-single">Single-tenant</button>
                  <button onClick={() => setNotifConfig(c => ({ ...c, teams_bot_app_type: 'MultiTenant' }))} className={`px-2 py-1 rounded text-[9px] flex-1 ${notifConfig.teams_bot_app_type === 'MultiTenant' ? 'bg-primary/20 text-primary' : 'bg-muted/50 text-muted-foreground'}`} data-testid="teams-apptype-multi">Multi-tenant</button>
                </div>
              </div>
              {(notifConfig.teams_bot_app_type || 'SingleTenant') === 'SingleTenant' && (
                <div><Label className="text-[10px] text-muted-foreground">Directory (Tenant) ID</Label><Input value={notifConfig.teams_bot_tenant_id || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_bot_tenant_id: e.target.value }))} placeholder="directory (tenant) id" className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" data-testid="teams-bot-tenant-id" /></div>
              )}
            </div>

            {teamsAi?.messaging_endpoint && (
              <div className="rounded-lg bg-muted/40 border border-border/40 p-2.5 space-y-1">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/80 font-semibold">Messaging endpoint (paste into your Azure Bot)</p>
                <div className="flex items-center gap-2">
                  <code className="text-[9px] font-mono text-foreground/80 truncate flex-1 bg-background/60 rounded px-1.5 py-0.5">{teamsAi.messaging_endpoint}</code>
                  <button onClick={() => copyText(teamsAi.messaging_endpoint)} className="text-muted-foreground hover:text-primary" data-testid="copy-teams-endpoint"><Copy className="w-3 h-3" /></button>
                </div>
              </div>
            )}

            {teamsAi?.connected && (
              <div className="rounded-lg bg-muted/40 border border-border/40 p-2.5 space-y-1" data-testid="teams-inbound-diagnostics">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground/80 font-semibold">Bot message diagnostics</p>
                <p className="text-[10px] text-muted-foreground">
                  Last message received from Teams:{' '}
                  <span className="text-foreground font-medium">{teamsAi.last_inbound_at ? new Date(teamsAi.last_inbound_at).toLocaleString() : 'never — Azure has not delivered a message to this endpoint yet'}</span>
                </p>
                {teamsAi.last_inbound_error ? (
                  <p className="text-[10px] text-red-400">Last error: <span className="font-mono">{teamsAi.last_inbound_error}</span></p>
                ) : teamsAi.last_inbound_at ? (
                  <p className="text-[10px] text-emerald-400">Last message processed without error.</p>
                ) : null}
                {!teamsAi.last_inbound_at && (
                  <p className="text-[9px] text-muted-foreground/70">If this stays "never" after you message the bot, Azure isn't reaching this endpoint — re-check the Messaging endpoint above and that the Teams channel is enabled on the Azure Bot.</p>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={connectTeamsAi} disabled={teamsAiBusy} className="rounded-lg text-[10px] h-7" data-testid="teams-ai-connect-btn">
                {teamsAiBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {teamsAi?.connected ? 'Re-verify connection' : 'Connect & Verify'}
              </Button>
              {teamsAi?.connected && (
                <Button variant="outline" size="sm" onClick={disconnectTeamsAi} disabled={teamsAiBusy} className="rounded-lg text-[10px] h-7" data-testid="teams-ai-disconnect-btn">Disconnect</Button>
              )}
              {teamsAi?.connected && (
                <Button variant="outline" size="sm" onClick={sendTeamsTest} disabled={teamsTestBusy} className="rounded-lg text-[10px] h-7" data-testid="teams-test-btn">
                  {teamsTestBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Send test message
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={runTeamsDiag} disabled={teamsTestBusy} className="rounded-lg text-[10px] h-7" data-testid="teams-diagnose-btn">
                {teamsTestBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Diagnose connection
              </Button>
              <Button variant="outline" size="sm" onClick={downloadTeamsAppPackage} className="rounded-lg text-[10px] h-7" data-testid="teams-app-package-btn">Download Teams app package (.zip)</Button>
              <Button variant="ghost" size="sm" onClick={downloadTeamsManifest} className="rounded-lg text-[10px] h-7" data-testid="teams-manifest-btn">manifest.json only</Button>
            </div>

            {teamsDiag && (
              <div className={`rounded-lg border p-3 text-[10px] leading-relaxed space-y-1.5 ${teamsDiag.ok ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-amber-500/5 border-amber-500/30'}`} data-testid="teams-diagnosis-panel">
                <div className="flex items-center justify-between">
                  <span className={`font-semibold text-[11px] ${teamsDiag.ok ? 'text-emerald-500' : 'text-amber-500'}`}>
                    {teamsDiag.ok ? 'Credentials valid' : 'Configuration issue found'}
                  </span>
                  <button onClick={() => setTeamsDiag(null)} className="text-muted-foreground hover:text-foreground" data-testid="teams-diagnosis-close">✕</button>
                </div>
                <p className="text-foreground">{teamsDiag.recommendation}</p>
                {teamsDiag.suggested_app_type && (
                  <Button size="sm" onClick={() => { setNotifConfig(c => ({ ...c, teams_bot_app_type: teamsDiag.suggested_app_type })); toast.info(`App type set to ${teamsDiag.suggested_app_type} — click Connect & Verify`); }} className="rounded-lg text-[10px] h-6 mt-1" data-testid="teams-diagnosis-apply-apptype">
                    Switch to {teamsDiag.suggested_app_type}
                  </Button>
                )}
                {!teamsDiag.ok && teamsDiag.error_description && (
                  <p className="text-muted-foreground font-mono text-[9px] break-words pt-1 border-t border-border/30">Azure: {teamsDiag.error_description}</p>
                )}
              </div>
            )}

            {teamsGuideOpen && (
              <div className="rounded-lg bg-background/60 border border-border/40 p-3 text-[10px] text-muted-foreground leading-relaxed space-y-2.5" data-testid="teams-ai-guide">
                <p className="text-foreground font-semibold text-[11px]">Step-by-step setup</p>
                <ol className="list-decimal ml-4 space-y-1.5">
                  <li>
                    <span className="text-foreground font-semibold">Register an app.</span> Go to <span className="font-mono">entra.microsoft.com</span> → <span className="font-semibold">App registrations → New registration</span>. Pick an account type and remember it — it must match the App type toggle above:
                    <ul className="list-disc ml-4 mt-0.5">
                      <li><span className="font-semibold">Single-tenant</span> ("this organizational directory only") → also copy the <span className="font-semibold">Directory (tenant) ID</span> into the field above.</li>
                      <li><span className="font-semibold">Multi-tenant</span> ("any organizational directory") → choose Multi-tenant above; no Directory ID needed.</li>
                    </ul>
                    From the app's <span className="font-semibold">Overview</span>, copy the <span className="font-semibold">Application (client) ID</span> (not the Object ID) into the App ID field above.
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Create a client secret.</span> App → <span className="font-semibold">Certificates &amp; secrets → New client secret</span>. Copy the secret <span className="font-semibold">Value</span> (shown only once — NOT the "Secret ID") into the Client secret field above. A wrong/expired value is the #1 cause of the "401 Unauthorized" error.
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Create the Azure Bot.</span> Azure portal → <span className="font-semibold">Create a resource → Azure Bot</span>. Give it a <span className="font-semibold">Bot handle</span>, and for <span className="font-semibold">Resource group</span> click <span className="font-semibold">Create new</span> (any name, e.g. <span className="font-mono">cloverleaf-bot-rg</span>) — an empty resource group is what triggers the "value must not be empty" error. Data residency <span className="font-semibold">Global</span>, pricing <span className="font-semibold">Free (F0)</span>. Choose <span className="font-semibold">Use existing app registration</span> and paste the same App ID. Click <span className="font-semibold">Review + create</span>.
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Set the messaging endpoint.</span> This is on the <span className="font-semibold">Azure Bot</span> resource in <span className="font-mono">portal.azure.com</span> — <span className="font-semibold">not</span> the App registration in <span className="font-mono">entra.microsoft.com</span> (that screen only shows Overview / Authentication / Certificates &amp; secrets and has no "Configuration"). Open the <span className="font-semibold">Azure Bot</span> resource itself (search "<span className="font-mono">{'{bot handle}'}</span>" in the top bar and pick the result whose type is <span className="font-semibold">Azure Bot</span> — not the resource group, not the App registration). In its menu → <span className="font-semibold">Settings → Configuration</span> → paste the <span className="font-semibold">Messaging endpoint</span> shown above (use the copy button) and <span className="font-semibold">Apply</span>.
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Enable the Teams channel.</span> Same Azure Bot resource → <span className="font-semibold">Settings → Channels</span> → click <span className="font-semibold">Microsoft Teams</span> → accept the terms → keep <span className="font-semibold">Microsoft Teams Commercial</span> → <span className="font-semibold">Apply</span>. It should then show under Configured channels.
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Package &amp; upload the app.</span> Easiest: click <span className="font-semibold">Download Teams app package (.zip)</span> above — Cloverleaf builds a ready-to-upload zip with your bot's App ID and both icons already inside (no manual zipping, so it avoids the macOS <span className="font-mono">__MACOSX</span> files that make Teams say "couldn't parse the manifest"). Then in Teams → open <span className="font-semibold">Apps</span> (in the new Teams this is the <span className="font-semibold">+</span> icon near the bottom of the left rail, or under the <span className="font-semibold">…</span> "More" menu — it's no longer a labelled "Apps" button) → <span className="font-semibold">Manage your apps → Upload an app → Upload a custom app</span> → pick the zip → <span className="font-semibold">Add</span>. <span className="text-muted-foreground">(Advanced: "manifest.json only" gives you just the file if you prefer to zip it yourself with your own icons — if you do, zip on Windows or via a tool that omits <span className="font-mono">__MACOSX/._</span> files, and keep <span className="font-mono">manifest.json</span> at the top level.)</span>
                    <div className="mt-1 rounded bg-primary/5 border border-primary/20 p-1.5">
                      Need icons? Download ready-made ones: <a href="/teams-icons/color.png" download target="_blank" rel="noreferrer" className="text-primary underline font-semibold">color.png</a> · <a href="/teams-icons/outline.png" download target="_blank" rel="noreferrer" className="text-primary underline font-semibold">outline.png</a> (or resize your own logo to those exact sizes and file names).
                    </div>
                  </li>
                  <li>
                    <span className="text-foreground font-semibold">Connect &amp; message.</span> Back in Cloverleaf, on this <span className="font-semibold">Settings → AI in Teams</span> panel, click <span className="font-semibold">Connect &amp; Verify</span> (there's no separate Save — this saves your entries, then validates them with Azure, and only marks the bot connected if Azure accepts). Then in Teams, open a 1:1 chat with the bot or <span className="font-semibold">@mention</span> it in a channel; it will reply with a link to connect your Cloverleaf account and pick a model.
                  </li>
                </ol>

                <div className="rounded bg-amber-500/5 border border-amber-500/20 p-2 space-y-1">
                  <p className="text-amber-600 dark:text-amber-300 font-semibold text-[10px]">If "Upload a custom app" is missing in Teams</p>
                  <p>It's blocked by admin policy. In the <span className="font-semibold">Teams admin center</span> (<span className="font-mono">admin.teams.microsoft.com</span> — the full site, not the lightweight "Admin" app inside Teams):</p>
                  <ul className="list-disc ml-4">
                    <li><span className="font-semibold">Teams apps → Manage apps → Org-wide app settings → Custom apps</span>: turn on "Let users install and use available apps by default" and "Let users interact with custom apps in preview".</li>
                    <li><span className="font-semibold">Teams apps → Setup policies → Global</span>: turn on <span className="font-semibold">Upload custom apps</span> → Save.</li>
                    <li>Also allow custom apps under <span className="font-semibold">Teams apps → Permission policies → Global</span>. Changes can take a few hours; sign out/in of Teams after.</li>
                  </ul>
                </div>

                <div className="rounded bg-red-500/5 border border-red-500/20 p-2 space-y-1">
                  <p className="text-red-600 dark:text-red-300 font-semibold text-[10px]">Troubleshooting "Azure rejected the credentials: 401 Unauthorized"</p>
                  <ul className="list-disc ml-4">
                    <li><span className="font-semibold">Secret Value vs ID</span> — paste the secret's <span className="font-semibold">Value</span> (shown once), not the Secret ID; regenerate if unsure.</li>
                    <li><span className="font-semibold">App type mismatch</span> — the App type toggle above must match the app registration's "Supported account types". A multi-tenant app with the Single-tenant setting (or vice versa) returns 401.</li>
                    <li><span className="font-semibold">Directory ID</span> — for single-tenant, it must be the app's home tenant (from the app Overview).</li>
                    <li><span className="font-semibold">App ID</span> — use the Application (client) ID, not the Object ID or the bot resource ID. Check for trailing spaces.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          <Separator className="bg-border/30" />

          {/* Teams */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-heading font-semibold text-foreground">Microsoft Teams</p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setSetupGuide((notifConfig.teams_mode || 'webhook') === 'api' ? 'teams_api' : 'teams_webhook')}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-medium transition-colors"
                  title="View step-by-step Teams setup guide"
                  data-testid="teams-setup-help-btn"
                >
                  <HelpCircle className="w-3 h-3" /> Setup guide
                </button>
                <button onClick={() => setNotifConfig(c => ({ ...c, teams_mode: 'webhook' }))} className={`px-2 py-0.5 rounded text-[9px] ${(notifConfig.teams_mode || 'webhook') === 'webhook' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}>Webhook</button>
                <button onClick={() => setNotifConfig(c => ({ ...c, teams_mode: 'api' }))} className={`px-2 py-0.5 rounded text-[9px] ${notifConfig.teams_mode === 'api' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}>Graph API</button>
              </div>
            </div>
            {(notifConfig.teams_mode || 'webhook') === 'webhook' ? (
              <div><Label className="text-[10px] text-muted-foreground">Webhook URL</Label><Input value={notifConfig.teams_webhook_url || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_webhook_url: e.target.value }))} placeholder="https://outlook.office.com/webhook/..." className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
            ) : (
              <><div><Label className="text-[10px] text-muted-foreground">Client ID</Label><Input value={notifConfig.teams_client_id || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_client_id: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
              <div><Label className="text-[10px] text-muted-foreground">Client Secret</Label><Input value={notifConfig.teams_client_secret || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_client_secret: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" type="password" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-[10px] text-muted-foreground">Tenant ID</Label><Input value={notifConfig.teams_tenant_id || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_tenant_id: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
                <div><Label className="text-[10px] text-muted-foreground">Team ID</Label><Input value={notifConfig.teams_team_id || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_team_id: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
                <div><Label className="text-[10px] text-muted-foreground">Channel ID</Label><Input value={notifConfig.teams_channel_id || ''} onChange={e => setNotifConfig(c => ({ ...c, teams_channel_id: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
              </div></>
            )}
            <Button variant="outline" size="sm" onClick={() => testChannel('teams')} className="rounded-lg text-[10px] h-7">Send Test Teams Message</Button>
          </div>
          <Separator className="bg-border/30" />

          {/* Twilio */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-heading font-semibold text-foreground">Twilio SMS</p>
              <button onClick={() => setSetupGuide('twilio')} className="p-1 rounded hover:bg-muted/30 text-muted-foreground hover:text-primary" title="Setup instructions"><HelpCircle className="w-3.5 h-3.5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-[10px] text-muted-foreground">Account SID</Label><Input value={notifConfig.twilio_account_sid || ''} onChange={e => setNotifConfig(c => ({ ...c, twilio_account_sid: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
              <div><Label className="text-[10px] text-muted-foreground">Auth Token</Label><Input value={notifConfig.twilio_auth_token || ''} onChange={e => setNotifConfig(c => ({ ...c, twilio_auth_token: e.target.value }))} className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" type="password" /></div>
              <div><Label className="text-[10px] text-muted-foreground">From Number</Label><Input value={notifConfig.twilio_from_number || ''} onChange={e => setNotifConfig(c => ({ ...c, twilio_from_number: e.target.value }))} placeholder="+1234567890" className="rounded-lg bg-muted/50 border-border/50 text-xs mt-0.5 font-mono" /></div>
            </div>
            <Button variant="outline" size="sm" onClick={() => testChannel('sms')} className="rounded-lg text-[10px] h-7">Send Test SMS</Button>
          </div>
          <Separator className="bg-border/30" />
          <Button onClick={saveNotifConfig} disabled={notifSaving} className="w-full rounded-lg bg-primary text-primary-foreground text-xs active:scale-95" data-testid="save-notif-config">
            {notifSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
            Save Notification Settings
          </Button>
        </div>
      )}

      {/* Feature Flags (E1-S4) */}
      {user?.role === 'admin' && Object.keys(flags).length > 0 && (
        <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '250ms' }}>
          <div className="flex items-center gap-3">
            <Flag className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <h2 className="text-base font-heading font-semibold text-foreground">Feature Flags</h2>
          </div>
          <Separator className="bg-border/50" />
          {Object.entries(flagsByCategory).map(([category, catFlags]) => (
            <div key={category} className="space-y-2">
              <p className="text-[10px] text-muted-foreground tracking-wider font-semibold">{formatFlagName(category)}</p>
              {catFlags.map(flag => (
                <div key={flag.name} className="flex items-center justify-between py-1.5" data-testid={`flag-${flag.name}`}>
                  <div>
                    <p className="text-xs text-foreground font-medium">{formatFlagName(flag.name)}</p>
                    <p className="text-[10px] text-muted-foreground">{flag.description}</p>
                  </div>
                  <Switch checked={flag.enabled} onCheckedChange={() => toggleFlag(flag.name, flag.enabled)} data-testid={`flag-toggle-${flag.name}`} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Tenant Data Export — admins only */}
      {(user?.role === 'admin' || user?.role === 'tenant_admin' || user?.role === 'super_admin') && (
        <div id="data-export" className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up scroll-mt-6" style={{ animationDelay: '275ms' }}>
          <TenantDataExport />
        </div>
      )}

      {/* Metrics (E20-S3) */}
      {metricsData && user?.role === 'admin' && (
        <div className="glass-card rounded-2xl p-6 space-y-4 animate-slide-up" style={{ animationDelay: '300ms' }}>
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <h2 className="text-base font-heading font-semibold text-foreground">Platform Metrics</h2>
          </div>
          <Separator className="bg-border/50" />
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground">Total Requests</p>
              <p className="text-lg font-heading font-bold text-foreground">{metricsData.requests_total}</p>
            </div>
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground">Errors</p>
              <p className="text-lg font-heading font-bold text-foreground">{metricsData.errors_total}</p>
            </div>
            <div className="p-3 rounded-xl bg-muted/30">
              <p className="text-xs text-muted-foreground">Error Rate</p>
              <p className="text-lg font-heading font-bold text-foreground">{metricsData.error_rate}%</p>
            </div>
          </div>
        </div>
      )}

      {/* MFA Setup Dialog */}
      <Dialog open={mfaDialogOpen} onOpenChange={setMfaDialogOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground text-base">Set Up MFA</DialogTitle>
          </DialogHeader>
          {mfaSetup && (
            <div className="space-y-4 mt-2">
              {mfaSetup.uri && (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-[11px] text-muted-foreground text-center">Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>
                  <div className="p-3 rounded-xl bg-white" data-testid="mfa-qr-code">
                    <QRCodeSVG value={mfaSetup.uri} size={168} level="M" includeMargin={false} />
                  </div>
                </div>
              )}
              <div className="p-3 rounded-xl bg-muted/30 text-center">
                <p className="text-[10px] text-muted-foreground mb-2">Can't scan? Enter this key manually:</p>
                <p className="font-mono text-xs text-foreground break-all select-all" data-testid="mfa-secret">{mfaSetup.secret}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Verification Code</Label>
                <Input
                  data-testid="mfa-verify-input"
                  value={mfaCode}
                  onChange={e => setMfaCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="rounded-lg bg-muted/50 border-border/50 text-center font-mono text-lg tracking-widest"
                />
              </div>
              <Button
                data-testid="confirm-mfa-btn"
                onClick={confirmMfa}
                disabled={mfaCode.length < 6 || mfaLoading}
                className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95"
              >
                {mfaLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Verify & Enable
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Setup Instructions Dialog */}
      <Dialog open={!!setupGuide} onOpenChange={() => setSetupGuide(null)}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-primary" />
              {setupGuide && SETUP_INSTRUCTIONS[setupGuide]?.title}
            </DialogTitle>
          </DialogHeader>
          {setupGuide && SETUP_INSTRUCTIONS[setupGuide] && (
            <div className="mt-3 space-y-3">
              {SETUP_INSTRUCTIONS[setupGuide].steps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">{i + 1}</div>
                  <p className="text-sm text-foreground/80 pt-0.5">{step}</p>
                </div>
              ))}
              {SETUP_INSTRUCTIONS[setupGuide].manifest && (
                <div className="rounded-lg border border-border/40 bg-muted/20 p-3" data-testid="setup-manifest-block">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80 font-semibold">App Manifest (paste into Slack)</p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(SETUP_INSTRUCTIONS[setupGuide].manifest); toast.success('Manifest copied to clipboard'); }}
                      className="text-[10px] text-primary hover:underline"
                      data-testid="copy-manifest-btn"
                    >Copy</button>
                  </div>
                  <pre className="text-[10px] font-mono text-foreground/85 whitespace-pre-wrap leading-relaxed max-h-48 overflow-auto">{SETUP_INSTRUCTIONS[setupGuide].manifest}</pre>
                </div>
              )}
              {SETUP_INSTRUCTIONS[setupGuide].note && (
                <div className="text-[11px] text-muted-foreground/80 leading-relaxed border-l-2 border-primary/30 pl-3 italic">
                  {SETUP_INSTRUCTIONS[setupGuide].note}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Distribution List Dialog */}
      <Dialog open={dlDialogOpen} onOpenChange={setDlDialogOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="dl-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editDL ? 'Edit Distribution List' : 'Create Distribution List'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs text-muted-foreground">List Name</Label><Input value={dlName} onChange={e => setDlName(e.target.value)} placeholder="e.g. Underwriting Team" className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" data-testid="dl-name-input" /></div>
            <div><Label className="text-xs text-muted-foreground">Description</Label><Input value={dlDesc} onChange={e => setDlDesc(e.target.value)} placeholder="Who this list is for" className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" /></div>
            <div><Label className="text-xs text-muted-foreground">Email Addresses (comma-separated)</Label><textarea value={dlEmails} onChange={e => setDlEmails(e.target.value)} placeholder="john@company.com, jane@company.com" className="w-full rounded-lg bg-muted/50 border border-border/50 mt-1 p-2 text-sm text-foreground min-h-[80px] resize-none outline-none focus:ring-1 focus:ring-primary/50" data-testid="dl-emails-input" /></div>
            <Button onClick={saveDL} disabled={!dlName.trim() || !dlEmails.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="save-dl-btn">{editDL ? 'Save Changes' : 'Create List'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
